import * as React from 'react';

import {
  isAuthenticationResponse,
  isInvalidCredentialsError,
  isOrganizationSelectionError,
} from '@rematter/frontend-lib-api-client';
import { Button, OTPField } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom } from 'jotai';

import { $signIn, OrganizationRecord } from '../state/signIn.state';

import { useTranslations } from '~/app/i18n/useTranslations';
import { config } from '~/config';
import { useApi, useIdentity } from '~/shared/hooks';

export const EmailVerification = () => {
  const toast = useToast();
  const t = useTranslations();
  const [signInState, setSignInState] = useAtom($signIn);
  const [verificationCode, setVerificationCode] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { setAccessToken, setOrganization, setUser } = useIdentity();
  const { client } = useApi();

  const handleVerification = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (
      !signInState.pendingAuthenticationToken ||
      verificationCode.length !== 6
    )
      return;

    setIsSubmitting(true);

    try {
      const { authenticationVerifyEmail } = await client(
        'mutation.authentication.verifyEmail',
      ).mutation({
        authenticationVerifyEmail: {
          __typename: true,
          __args: {
            clientId: config.workOs.clientId ?? '',
            pendingAuthenticationToken: signInState.pendingAuthenticationToken,
            code: verificationCode,
          },
          on_OrganizationSelectionError: {
            pendingAuthenticationToken: true,
            organizations: {
              id: true,
              name: true,
            },
          },
          on_AuthenticationResponse: {
            accessToken: true,
          },
        },
      });

      if (isAuthenticationResponse(authenticationVerifyEmail)) {
        setSignInState({ ...signInState, authenticated: true });
        setAccessToken(authenticationVerifyEmail.accessToken);
        setUser(authenticationVerifyEmail.user);
        setOrganization(authenticationVerifyEmail.organization);
        return;
      }

      if (isOrganizationSelectionError(authenticationVerifyEmail)) {
        setSignInState({
          ...signInState,
          step: 'org-selection',
          organizations:
            authenticationVerifyEmail.organizations as OrganizationRecord[],
          pendingAuthenticationToken:
            authenticationVerifyEmail.pendingAuthenticationToken,
        });
        return;
      }

      if (isInvalidCredentialsError(authenticationVerifyEmail)) {
        toast.error(t.emailVerification.errors.invalid);
      }
    } catch (err) {
      toast.error(String(err));
      // Reset to credentials step on error
      setSignInState({
        step: 'credentials',
        authenticated: false,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleButtonPress = () => {
    handleVerification();
  };

  const handleBack = () => {
    setSignInState({
      ...signInState,
      step: 'credentials',
      pendingAuthenticationToken: undefined,
    });
  };

  return (
    <form
      onSubmit={handleVerification}
      className="@EmailVerification flex flex-col gap-5"
    >
      <div className="flex flex-col gap-6">
        <h2 className="text-center text-xl font-semibold text-neutral/80">
          {t.emailVerification.title}
        </h2>

        <p className="text-center text-neutral">
          {t.emailVerification.subtitle}
        </p>

        <OTPField
          onChange={setVerificationCode}
          isDisabled={isSubmitting}
          autoFocus={true}
          length={6}
        />

        <div className="flex flex-col gap-3">
          <Button
            onPress={handleButtonPress}
            isDisabled={isSubmitting || verificationCode.length !== 6}
            className="w-full"
          >
            {t.emailVerification.submit}
          </Button>

          <Button
            onPress={handleBack}
            isDisabled={isSubmitting}
            className="w-full"
          >
            {t.emailVerification.goBack}
          </Button>
        </div>
      </div>
    </form>
  );
};
